"use client";
import React, { useEffect, useState } from "react";
import { Divider, Drawer, Modal, Grid } from "antd";
const { useBreakpoint } = Grid;
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { usePathname, useRouter } from "next/navigation";

import LookupPhoneNumber from "./LookupPhoneNumber";
import RescheduleMain from "./RescheduleMain";
import FirstClient from "./FirstClient";
import RescheduleList from "./RescheduleList";
import SetupPayment from "./SetupPayment";

import { isOpeningsBackedAppointment } from "@/lib/api/openingsLocations";
import { useApp } from "@/lib/hooks/useAppointment";
import useCoarsePointer from "@/lib/hooks/useCoarsePointer";
import { extractErrorMessage } from "@/lib/helpers";
import {
  ICreateAppointmentArgs,
  IRescheduleAppointmentArgs,
} from "@/lib/api/types";
import { pushToDataLayer, trackBookAppointment } from "@/lib/gtm";
import { useBookingApi } from "./bookingApi";
import { servicesRequirePayment } from "./paymentRequirement";
import {
  RepricedServicesProvider,
  useRepricedServicesController,
} from "./useRepricedServices";

const stripeKey = process.env.NEXT_PUBLIC_OPENINGS_STRIPE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

export const LOOKUP_PHONE_NUMBER = "lookup";
export const SETUP_PAYMENT = "setup-payment";
export const FIRST_TIME_CLIENT = "first-time-client";
export const RESCHEDULE_MAIN = "reschedule-main";
export const RESCHEDULE_LIST = "reschedule-list";

export type IPathState =
  | typeof LOOKUP_PHONE_NUMBER
  | typeof FIRST_TIME_CLIENT
  | typeof SETUP_PAYMENT
  | typeof RESCHEDULE_MAIN
  | typeof RESCHEDULE_LIST;

interface AppointmentArgs {
  paymentMethodId?: string;
  errorCallback: (errorMessage?: string) => void;
}

export interface INewAppointmentArgs extends AppointmentArgs {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  code?: string;
}

export interface IRescheduleArgs extends AppointmentArgs {
  previousAppointmentId: string;
  phoneNumber?: string;
  verificationCode?: string;
}

export type IAppointmentRouteState = {
  upcomingAppointments: any[];
  phoneNumber: string;
  hasUpcomingAppointments?: boolean;
  isCreditCardRequired?: boolean;
  verifyForUpcomingAppointments?: boolean;
  returnToRescheduleList?: boolean;
  listAlreadyLoaded?: boolean;
  verificationPurpose?: string;
  verifyDuringAppointmentCreate?: boolean;
  verificationResendAvailableAt?: number;
  verificationCode?: string;
  /** Collected on the identity step and carried into the card step. */
  firstName?: string;
  lastName?: string;
  client: {
    customerId?: string;
    firstName: string;
    lastName: string;
    blocked?: boolean;
    depositRequired?: boolean;
  } | null;
  paymentMethodId?: string;
};

const initialRoute = {
  path: LOOKUP_PHONE_NUMBER as IPathState,
  state: {
    upcomingAppointments: [],
    phoneNumber: "",
    client: {
      firstName: "Unknown",
      lastName: "Unknown",
      isBlocked: false,
    },
  },
};

const VERIFICATION_RESEND_COOLDOWN_SECONDS = 45;
const PRICING_NOT_READY_MESSAGE =
  "Please confirm current service pricing before continuing.";

const isVerificationRequiredError = (error: any) => {
  if (error?.status === 428) return true;

  const bodyError = error?.body?.error;
  if (bodyError === "VERIFICATION_REQUIRED") return true;

  if (typeof error?.message !== "string") return false;

  try {
    const parsed = JSON.parse(error.message);
    return parsed?.error === "VERIFICATION_REQUIRED";
  } catch {
    return error.message.includes("Verification code required");
  }
};

const NewAppointmentModal = ({ devHarness = false }: { devHarness?: boolean }) => {
  const screens = useBreakpoint();
  const coarsePointer = useCoarsePointer();
  const pathname = usePathname();
  const router = useRouter();
  const { isAppointmentModalActive, appointmentForm, closeAppointmentForm } =
    useApp();
  const bookingApi = useBookingApi();

  // The /dev booking harness mounts its own instance inside a mocked
  // BookingApiProvider. The root-layout instance must stand down there, or
  // both instances would open off the same AppContext — and, since GTM
  // tracking runs in an effect below (not the render-time early return
  // further down), it must be able to check this before that effect fires,
  // or both instances would double-push `booking_modal_opened` on
  // /dev/booking-flow. An exact-or-subpath check so this never also matches
  // a future route like `/developers`.
  const isSuppressedOnDevRoute =
    !devHarness && (pathname === "/dev" || pathname.startsWith("/dev/"));
  // Mounted exactly once above every modal route. The suppressed root-layout
  // instance on /dev must receive no appointment, or it would issue a real
  // pricing request alongside the harness instance's mocked one.
  const pricing = useRepricedServicesController(
    isSuppressedOnDevRoute ? undefined : appointmentForm,
  );
  const [route, setRoute] = useState<{
    path: IPathState;
    state: IAppointmentRouteState;
  }>(initialRoute);

  const onRouteChange = (path: IPathState, state: any) => {
    setRoute((prevRoute) => ({
      path,
      state: { ...prevRoute.state, ...state },
    }));
  };

  const handleAppointment = async <
    TArgs extends ICreateAppointmentArgs | IRescheduleAppointmentArgs,
    TResult extends { appointmentId: string },
  >(
    action: (args: TArgs) => Promise<TResult>,
    args: AppointmentArgs,
    additionalFields: any,
    onSuccess?: (result: TResult) => void,
  ) => {
    if (!appointmentForm) return;
    // Defense in depth: route components disable their actions while pricing
    // is pending/error, but no programmatic caller may reach a write either.
    if (pricing.status !== "ready") {
      args.errorCallback(PRICING_NOT_READY_MESSAGE);
      return;
    }
    try {
      const { userId, scheduleId, time, date, services, requestId, onAfter } =
        appointmentForm;

      const res = await action({
        userId,
        locationId: appointmentForm.locationId,
        scheduleId,
        time,
        date,
        services: services.map((item) => ({
          _id: item._id,
          category: item.category,
          ...(item.option && { optionId: item.option.id }),
        })),
        ...(args.paymentMethodId && { paymentMethodId: args.paymentMethodId }),
        ...(requestId && { requestId }),
        ...additionalFields,
      } as TArgs);

      onSuccess?.(res);

      if (onAfter) {
        onAfter(res.appointmentId);
        closeAppointmentForm();
        setRoute(initialRoute);
      } else {
        router.push(`/appointment/review/${res.appointmentId}`); // Wait for the routing to complete
      }
    } catch (e: any) {
      if (
        isOpeningsBackedAppointment(appointmentForm) &&
        isVerificationRequiredError(e) &&
        "phoneNumber" in additionalFields &&
        additionalFields.phoneNumber &&
        !additionalFields.code
      ) {
        try {
          await bookingApi.sendVerificationCode(
            additionalFields.phoneNumber,
            appointmentForm,
          );
          onRouteChange(FIRST_TIME_CLIENT, {
            phoneNumber: additionalFields.phoneNumber,
            client: route.state.client,
            isCreditCardRequired: servicesRequirePayment(pricing.services),
            paymentMethodId: args.paymentMethodId,
            verifyDuringAppointmentCreate: true,
            verificationResendAvailableAt:
              Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
          });
          args.errorCallback();
        } catch (sendError) {
          args.errorCallback(extractErrorMessage(sendError));
        }
        return;
      }

      args.errorCallback(extractErrorMessage(e));
    }
  };

  const onCreate = async (args: INewAppointmentArgs) => {
    await handleAppointment<
      ICreateAppointmentArgs,
      Awaited<ReturnType<typeof bookingApi.createAppointment>>
    >(
      bookingApi.createAppointment,
      args,
      {
        firstName: args.firstName,
        lastName: args.lastName,
        code: args.code,
        phoneNumber: args.phoneNumber,
      },
      (result) => {
        if (result.conversion) {
          trackBookAppointment(result.conversion);
        }
      },
    );
  };

  const onReschedule = async (args: IRescheduleArgs) => {
    await handleAppointment<
      IRescheduleAppointmentArgs,
      Awaited<ReturnType<typeof bookingApi.rescheduleAppointment>>
    >(
      bookingApi.rescheduleAppointment,
      args,
      {
        rescheduleAppointmentId: args.previousAppointmentId,
        ...(args.phoneNumber && { phoneNumber: args.phoneNumber }),
        ...(args.verificationCode && {
          verificationCode: args.verificationCode,
        }),
      },
    );
  };

  const componentMap: Record<IPathState, React.FC<any>> = {
    [LOOKUP_PHONE_NUMBER]: LookupPhoneNumber,
    [SETUP_PAYMENT]: SetupPayment,
    [FIRST_TIME_CLIENT]: FirstClient,
    [RESCHEDULE_MAIN]: RescheduleMain,
    [RESCHEDULE_LIST]: RescheduleList,
  };

  useEffect(() => {
    if (!isAppointmentModalActive) {
      setRoute(initialRoute);
    }
  }, [isAppointmentModalActive]);

  useEffect(() => {
    // The suppressed (root-layout, on /dev routes) instance must stay inert
    // here even though it still renders and runs effects: otherwise it and
    // the harness's own instance both push this event for the same open.
    if (isSuppressedOnDevRoute) return;
    if (isAppointmentModalActive) {
      pushToDataLayer("booking_modal_opened", {
        source: "appointment_modal",
        stylist_name: appointmentForm?.stylistName || "any",
        location_name: appointmentForm?.locationName || "any",
        service_type: appointmentForm?.services
          .map((service) => service.title)
          .join(", "),
        selected_date: appointmentForm?.date || "any",
        selected_time: appointmentForm?.time || "any",
      });
    }
  }, [isAppointmentModalActive, pathname, isSuppressedOnDevRoute]);

  useEffect(() => {
    if (pathname.includes("/appointment/review")) {
      closeAppointmentForm();
      setRoute(initialRoute);
    }
  }, [pathname]);

  // Placed after all hooks so the hook order stays unconditional.
  if (isSuppressedOnDevRoute) return null;

  const CurrentComponent = componentMap[route?.path];
  return (
    <RepricedServicesProvider value={pricing}>
      {/* Touch devices get the fullscreen drawer even at desktop widths. */}
      {screens.md && !coarsePointer ? (
        <Modal
          title="Appointment"
          open={isAppointmentModalActive}
          mask={{ closable: false }}
          onCancel={closeAppointmentForm}
          footer={null}
          style={{ top: 20 }}
          destroyOnHidden
        >
          <Divider dashed />
          <Elements
            stripe={stripePromise}
            options={{
              appearance: { theme: "stripe" },
              mode: "setup",
              currency: "usd",
              paymentMethodTypes: ["card"],
            }}
          >
            <CurrentComponent
              appointment={appointmentForm}
              routeState={route.state}
              onCreate={onCreate}
              onReschedule={onReschedule}
              onRouteChange={onRouteChange}
            />
          </Elements>
        </Modal>
      ) : (
        <Drawer
          title="Appointment"
          styles={{
            wrapper: {
              height: "100%",
            },
            mask: {
              background: "transparent",
            },
          }}
          autoFocus={false}
          placement="bottom"
          destroyOnHidden
          open={isAppointmentModalActive}
          onClose={closeAppointmentForm}
        >
          <Elements
            stripe={stripePromise}
            options={{
              appearance: { theme: "stripe" },
              mode: "setup",
              currency: "usd",
              paymentMethodTypes: ["card"],
            }}
          >
            <CurrentComponent
              appointment={appointmentForm}
              routeState={route.state}
              onCreate={onCreate}
              onReschedule={onReschedule}
              onRouteChange={onRouteChange}
            />
          </Elements>
        </Drawer>
      )}
    </RepricedServicesProvider>
  );
};

export default NewAppointmentModal;
